import { Measure } from '../types'
import Big from 'big.js'

enum RadioactiveDecayMeasurementSystem {
  siUnits = 'SI Units',
  curieUnits = 'Curie Units',
  otherUnits = 'Others Units'
}

const radioactiveDecayUnits = {
  becquerel: {
    codeName: 'becquerel',
    displayName: 'Becquerel',
    url: 'becquerel',
    symbol: 'Bq',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: RadioactiveDecayMeasurementSystem.siUnits
  },
  kilobecquerel: {
    codeName: 'kilobecquerel',
    displayName: 'Kilobecquerel',
    url: 'kilobecquerel',
    symbol: 'kBq',
    toBaseUnit: (value: Big) => value.times(1000),
    fromBaseUnit: (value: Big) => value.div(1000),
    system: RadioactiveDecayMeasurementSystem.siUnits
  },
  megabecquerel: {
    codeName: 'megabecquerel',
    displayName: 'Megabecquerel',
    url: 'megabecquerel',
    symbol: 'MBq',
    toBaseUnit: (value: Big) => value.times(1000000),
    fromBaseUnit: (value: Big) => value.div(1000000),
    system: RadioactiveDecayMeasurementSystem.siUnits
  },
  gigabecquerel: {
    codeName: 'gigabecquerel',
    displayName: 'Gigabecquerel',
    url: 'gigabecquerel',
    symbol: 'GBq',
    toBaseUnit: (value: Big) => value.times(1000000000),
    fromBaseUnit: (value: Big) => value.div(1000000000),
    system: RadioactiveDecayMeasurementSystem.siUnits
  },
  terabecquerel: {
    codeName: 'terabecquerel',
    displayName: 'Terabecquerel',
    url: 'terabecquerel',
    symbol: 'TBq',
    toBaseUnit: (value: Big) => value.times(1000000000000),
    fromBaseUnit: (value: Big) => value.div(1000000000000),
    system: RadioactiveDecayMeasurementSystem.siUnits
  },
  disintegrations_per_second: {
    codeName: 'disintegrations_per_second',
    displayName: 'Disintegrations per second',
    url: 'disintegrations-per-second',
    symbol: 'dps',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: RadioactiveDecayMeasurementSystem.siUnits
  },

  picocurie: {
    codeName: 'picocurie',
    displayName: 'Picocurie',
    url: 'picocurie',
    symbol: 'pCi',
    toBaseUnit: (value: Big) => value.div(27.027027027027),
    fromBaseUnit: (value: Big) => value.times(27.027027027027),
    system: RadioactiveDecayMeasurementSystem.curieUnits
  },
  nanocurie: {
    codeName: 'nanocurie',
    displayName: 'Nanocurie',
    url: 'nanocurie',
    symbol: 'nCi',
    toBaseUnit: (value: Big) => value.div(0.027027027027027),
    fromBaseUnit: (value: Big) => value.times(0.027027027027027),
    system: RadioactiveDecayMeasurementSystem.curieUnits
  },
  microcurie: {
    codeName: 'microcurie',
    displayName: 'Microcurie',
    url: 'microcurie',
    symbol: 'µCi',
    toBaseUnit: (value: Big) => value.div(2.7027027027027E-05),
    fromBaseUnit: (value: Big) => value.times(2.7027027027027E-05),
    system: RadioactiveDecayMeasurementSystem.curieUnits
  },
  millicurie: {
    codeName: 'millicurie',
    displayName: 'Millicurie',
    url: 'millicurie',
    symbol: 'mCi',
    toBaseUnit: (value: Big) => value.div(2.7027027027027E-08),
    fromBaseUnit: (value: Big) => value.times(2.7027027027027E-08),
    system: RadioactiveDecayMeasurementSystem.curieUnits
  },
  curie: {
    codeName: 'curie',
    displayName: 'Curie',
    url: 'curie',
    symbol: 'Ci',
    toBaseUnit: (value: Big) => value.div(2.7027027027027E-11),
    fromBaseUnit: (value: Big) => value.times(2.7027027027027E-11),
    system: RadioactiveDecayMeasurementSystem.curieUnits
  },
  kilocurie: {
    codeName: 'kilocurie',
    displayName: 'Kilocurie',
    url: 'kilocurie',
    symbol: 'kCi',
    toBaseUnit: (value: Big) => value.div(2.7027027027027E-14),
    fromBaseUnit: (value: Big) => value.times(2.7027027027027E-14),
    system: RadioactiveDecayMeasurementSystem.curieUnits
  },


  disintegrations_per_minute: {
    codeName: 'disintegrations_per_minute',
    displayName: 'Disintegrations per minute',
    url: 'disintegrations-per-minute',
    symbol: 'dpm',
    toBaseUnit: (value: Big) => value.times(0.0166666666666667),
    fromBaseUnit: (value: Big) => value.div(0.0166666666666667),
    system: RadioactiveDecayMeasurementSystem.otherUnits
  },
  rutherford: {
    codeName: 'rutherford',
    displayName: 'Rutherford',
    url: 'rutherford',
    symbol: 'Rd',
    toBaseUnit: (value: Big) => value.times(1000000),
    fromBaseUnit: (value: Big) => value.div(1000000),
    system: RadioactiveDecayMeasurementSystem.otherUnits
  }
}

export const radioactiveDecay: Measure = {
  displayName: 'Radioactivity (decay)',
  codeName: 'radioactiveDecay',
  url: 'radioactive-decay',
  units: radioactiveDecayUnits,
  baseUnit: radioactiveDecayUnits.becquerel,
  defaultFromUnit: radioactiveDecayUnits.becquerel,
  defaultToUnit: radioactiveDecayUnits.kilobecquerel
}
